import { getColor } from '@crm/components/Charts/utils'
import { abbreviateNumber, composePrice } from '@crm/utils'
import { TableContainer } from '@shared/components'
import { useLocales } from '@shared/locales'
import { Card, Col, Flex, Row, TableColumnsType } from 'antd'
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import {
  contractsByStatus,
  contractsTimeData,
  locationPerformance,
  mediaTypeDistribution,
  offersByStatus,
  offersTimeData,
  opportunitiesByStatus,
  opportunitiesTimeData,
  paymentAccountsByMonth,
  productsByFormat,
  revenueByMonth
} from './mockData'

export const TrendsetterReports = () => {
  const { t } = useLocales()

  return (
    <Flex vertical gap={24}>
      {/* Revenue Overview - Composed Chart */}
      <Card
        title={t('reports.trendsetter.revenue_overview') || 'Revenue Overview'}
      >
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={revenueByMonth}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis
              yAxisId="left"
              tickFormatter={(value) => abbreviateNumber(value)}
            />
            <Tooltip
              formatter={(value: number) => `${abbreviateNumber(value)} EUR`}
            />
            <Legend />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="revenue"
              fill={getColor(0)}
              fillOpacity={0.3}
              stroke={getColor(0)}
              name="Total Revenue"
            />
            <Bar
              yAxisId="left"
              dataKey="contracts"
              fill={getColor(1)}
              name="Contracts"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="offers"
              stroke={getColor(2)}
              strokeWidth={2}
              name="Offers"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      {/* Opportunities and Offers Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              t('reports.trendsetter.opportunities_over_time') ||
              'Opportunities Over Time'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={opportunitiesTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => abbreviateNumber(value)} />
                <Tooltip
                  formatter={(value: number) =>
                    `${abbreviateNumber(value)} EUR`
                  }
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={getColor(0)}
                  fill={getColor(0)}
                  fillOpacity={0.6}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              t('reports.trendsetter.offers_over_time') || 'Offers Over Time'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={offersTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => abbreviateNumber(value)} />
                <Tooltip
                  formatter={(value: number) =>
                    `${abbreviateNumber(value)} EUR`
                  }
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={getColor(1)}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Status Distribution Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card
            title={
              t('reports.trendsetter.opportunities_by_status') ||
              'Opportunities by Status'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={opportunitiesByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {opportunitiesByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              t('reports.trendsetter.offers_by_status') || 'Offers by Status'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={offersByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {offersByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              t('reports.trendsetter.contracts_by_status') ||
              'Contracts by Status'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={contractsByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {contractsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Products and Contracts Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              t('reports.trendsetter.products_by_format') ||
              'Products by Format'
            }
          >
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={productsByFormat}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis tickFormatter={(value) => abbreviateNumber(value)} />
                <Tooltip
                  formatter={(value: number) =>
                    `${abbreviateNumber(value)} EUR`
                  }
                />
                <Bar dataKey="value" fill={getColor(2)} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              t('reports.trendsetter.contracts_over_time') ||
              'Contracts Over Time'
            }
          >
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={contractsTimeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => abbreviateNumber(value)} />
                <Tooltip
                  formatter={(value: number) =>
                    `${abbreviateNumber(value)} EUR`
                  }
                />
                <Bar dataKey="value" fill={getColor(3)} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Media Type and Location Performance Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              t('reports.trendsetter.media_type_distribution') ||
              'Media Type Distribution'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={mediaTypeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {mediaTypeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              t('reports.trendsetter.location_performance') ||
              'Location Performance'
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={locationPerformance} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tickFormatter={(value) => abbreviateNumber(value)}
                />
                <YAxis dataKey="location" type="category" width={120} />
                <Tooltip
                  formatter={(value: number) =>
                    `${abbreviateNumber(value)} EUR`
                  }
                />
                <Bar dataKey="value" fill={getColor(4)} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Payment Accounts Table */}
      <Card
        title={t('reports.trendsetter.payment_accounts') || 'Payment Accounts'}
      >
        <PaymentAccountsTable />
      </Card>
    </Flex>
  )
}

const PaymentAccountsTable = () => {
  const { t } = useLocales()

  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec'
  ] as const

  const monthLabels = [
    t('reports.trendsetter.month_jan') || 'January',
    t('reports.trendsetter.month_feb') || 'February',
    t('reports.trendsetter.month_mar') || 'March',
    t('reports.trendsetter.month_apr') || 'April',
    t('reports.trendsetter.month_may') || 'May',
    t('reports.trendsetter.month_jun') || 'June',
    t('reports.trendsetter.month_jul') || 'July',
    t('reports.trendsetter.month_aug') || 'August',
    t('reports.trendsetter.month_sep') || 'September',
    t('reports.trendsetter.month_oct') || 'October',
    t('reports.trendsetter.month_nov') || 'November',
    t('reports.trendsetter.month_dec') || 'December'
  ]

  // Get cell background color based on amount
  const getCellColor = (amount: number): string => {
    if (amount === 3500) return '#d9f7be' // Green
    if (amount >= 1000 && amount < 3500) return '#fffbe6' // Yellow
    if (amount < 1000) return '#ffe7ba' // Orange
    return '#fff' // Default white
  }

  const columns: TableColumnsType<(typeof paymentAccountsByMonth)[0]> = [
    {
      title: t('reports.trendsetter.client') || 'Company Name',
      dataIndex: 'client',
      key: 'client',
      fixed: 'left',
      width: 150
    },
    ...months.map((month, index) => ({
      title: monthLabels[index],
      key: month,
      children: [
        {
          title: t('reports.trendsetter.offers') || 'Oferte',
          key: `${month}_offers`,
          width: 100,
          align: 'center' as const,
          render: (_: any, record: (typeof paymentAccountsByMonth)[0]) => {
            const monthData = record[month]
            const bgColor = getCellColor(monthData.totalAmount)

            return (
              <div
                style={{
                  backgroundColor: bgColor,
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: 500
                }}
              >
                {monthData.offersCount}
              </div>
            )
          }
        },
        {
          title: t('reports.trendsetter.total_amount') || 'Suma',
          key: `${month}_suma`,
          width: 120,
          align: 'center' as const,
          render: (_: any, record: (typeof paymentAccountsByMonth)[0]) => {
            const monthData = record[month]
            const bgColor = getCellColor(monthData.totalAmount)

            return (
              <div
                style={{
                  backgroundColor: bgColor,
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: 500
                }}
              >
                {composePrice(monthData.totalAmount, 'EUR')}
              </div>
            )
          }
        },
        {
          title: t('reports.trendsetter.paid_amount') || 'Achitat',
          key: `${month}_achitat`,
          width: 100,
          align: 'center' as const,
          render: (_: any, record: (typeof paymentAccountsByMonth)[0]) => {
            const monthData = record[month]

            return (
              <div
                style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontWeight: 500
                }}
              >
                {monthData.paidAmount}
              </div>
            )
          }
        }
      ]
    })),
    {
      title: t('reports.trendsetter.total') || 'Total',
      key: 'total',
      fixed: 'right',
      width: 120,
      align: 'right' as const,
      render: (_: any, record: (typeof paymentAccountsByMonth)[0]) => {
        const totalPaid =
          record.jan.paidAmount +
          record.feb.paidAmount +
          record.mar.paidAmount +
          record.apr.paidAmount +
          record.may.paidAmount +
          record.jun.paidAmount +
          record.jul.paidAmount +
          record.aug.paidAmount +
          record.sep.paidAmount +
          record.oct.paidAmount +
          record.nov.paidAmount +
          record.dec.paidAmount
        const totalAmount =
          record.jan.totalAmount +
          record.feb.totalAmount +
          record.mar.totalAmount +
          record.apr.totalAmount +
          record.may.totalAmount +
          record.jun.totalAmount +
          record.jul.totalAmount +
          record.aug.totalAmount +
          record.sep.totalAmount +
          record.oct.totalAmount +
          record.nov.totalAmount +
          record.dec.totalAmount
        const balance = totalPaid - totalAmount

        return (
          <div
            style={{
              padding: '8px',
              textAlign: 'right',
              fontWeight: 600,
              color: balance < 0 ? '#ff4d4f' : balance > 0 ? '#52c41a' : '#000'
            }}
          >
            {balance}
          </div>
        )
      }
    }
  ]

  return (
    <TableContainer
      columns={columns}
      dataSource={paymentAccountsByMonth}
      rowKey="client"
      scroll={{ x: 'max-content' }}
      pagination={false}
    />
  )
}
